import React, { useState } from 'react';
import { FilterOutlined } from '@ant-design/icons';
import { Badge, Button, Card, Col, Divider, Drawer, Flex, Input, Modal, Row, Select, Space, Statistic, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import RangePicker from 'components/date-range-picker';
import ProductPicker from 'components/pickers/product';
import { formatCurrency } from 'utils/number';
import { formatDate } from 'utils/date';

const { Search } = Input;
const { Text } = Typography;

const getSelectedCount = (value) => {
  if (Array.isArray(value)) {
    return value.length;
  }

  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0 ? 1 : 0;
  }

  return 1;
};

const getOrderStatusTags = (record) => {
  if (record.status === 'canceled') {
    return [<Tag key='canceled' color='error'>Đã huỷ</Tag>];
  }

  if (record.status === 'finalized') {
    return [<Tag key='finalized' color='success'>Đã hoàn tất</Tag>];
  }

  return [
    record.payment_status?.title ? <Tag key='payment' color='gold'>{record.payment_status.title}</Tag> : null,
    record.shipping_status?.title ? <Tag key='shipping' color='blue'>{record.shipping_status.title}</Tag> : null,
    record.internal_status?.title ? <Tag key='internal' color='purple'>{record.internal_status.title}</Tag> : null,
  ].filter(Boolean);
};

const ListMobile = ({ viewModel }) => {
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  const {
    data,
    filter,
    setFilterValue,
    onDateRangeFilterChange,
    onSearch,
    navigate,
    rangePickerPresets,
    rangePickerValue,
    summary,
    paymentStatusOptions,
    shippingStatusOptions,
    internalStatuses,
  } = viewModel;

  const activeFilterCount = [
    filter?.status && filter.status !== 'all' ? 1 : 0,
    filter?.create_on ? 1 : 0,
    filter?.payment_status_id && filter.payment_status_id !== 'all' ? 1 : 0,
    filter?.shipping_status_id && filter.shipping_status_id !== 'all' ? 1 : 0,
    filter?.internal_status_id && filter.internal_status_id !== 'all' ? 1 : 0,
    getSelectedCount(filter?.product_ids) > 0 ? 1 : 0,
  ].reduce((sum, current) => sum + current, 0);

  const onClearFilters = () => {
    setFilterValue('status', 'all');
    setFilterValue('create_on', null);
    setFilterValue('payment_status_id', 'all');
    setFilterValue('shipping_status_id', 'all');
    setFilterValue('internal_status_id', 'all');
    setFilterValue('product_ids', []);
    setShowFilterSheet(false);
  };

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <Card style={{ width: '100%' }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Flex gap={8} align='center'>
            <Search
              placeholder='Mã đơn hàng hoặc thông tin khách hàng'
              onSearch={onSearch}
              value={filter.info}
              onChange={(e) => setFilterValue('info', e.target.value)}
              style={{ flex: 1 }}
            />
            <Badge count={activeFilterCount} size='small' offset={[-4, 4]}>
              <Button icon={<FilterOutlined />} onClick={() => setShowFilterSheet(true)} />
            </Badge>
          </Flex>

          <Divider style={{ margin: '4px 0' }}></Divider>

          <Row gutter={[12, 12]}>
            <Col span={10}>
              <Card size='small'>
                <Statistic title='Đơn hàng' value={summary.totalOrders} />
              </Card>
            </Col>
            <Col span={14}>
              <Card size='small'>
                <Statistic title='Doanh số' value={summary.totalAmount} precision={0} />
              </Card>
            </Col>
          </Row>

          <Flex justify='flex-end'>
            <Button 
              type='primary' 
              onClick={() => navigate('/order/add')}
            >
              Thêm mới
            </Button>
          </Flex>
        </Space>
      </Card>

      <Space direction='vertical' size={8} style={{ width: '100%' }}>
        {(data || []).map((record) => (
          <Card key={record.id} style={{ width: '100%' }} bodyStyle={{ padding: 12 }}>
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
              <Flex justify='space-between' align='flex-start' gap={12}>
                <Space direction='vertical' size={2} style={{ minWidth: 0 }}>
                  <Text strong>Mã đơn: {record.id}</Text>
                  <Text type='secondary'>{formatDate(record.create_on)}</Text>
                </Space>
                <Button type='link' onClick={() => navigate(`/order/${record.id}`)}>
                  Chi tiết
                </Button>
              </Flex>

              <Flex vertical gap={4}>
                <Text strong ellipsis={{ tooltip: record.customer?.full_name || 'Khách lẻ' }}>
                  {record.customer?.full_name || 'Khách lẻ'}
                </Text>
                <Text type='secondary'>{record.customer?.phone || 'Không có số điện thoại'}</Text>
              </Flex>

              <Row gutter={[12, 8]}>
                <Col span={12}>
                  <Space direction='vertical' size={0}>
                    <Text type='secondary'>Tổng tiền</Text>
                    <Text strong>{formatCurrency(record.total_amount)}</Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space direction='vertical' size={0}>
                    <Text type='secondary'>Sản phẩm</Text>
                    <Text strong>{record.total_item}</Text>
                  </Space>
                </Col>
              </Row>

              <Flex wrap='wrap' gap={8}>
                {getOrderStatusTags(record)}
              </Flex>
            </Space>
          </Card>
        ))}
      </Space>

      {(!data || data.length === 0) && (
        <Card style={{ width: '100%', textAlign: 'center', padding: '24px 16px' }}>
          <Text type='secondary'>Không có đơn hàng nào</Text>
        </Card>
      )}

      <Drawer title='Bộ lọc' placement='bottom' height='auto' open={showFilterSheet} onClose={() => setShowFilterSheet(false)}>
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div>
                <label>Trạng thái</label>
                <Select value={filter.status} style={{ width: '100%' }} onChange={(value) => setFilterValue('status', value)}>
                  <Select.Option value='proccesing'>Đang xử lý</Select.Option>
                  <Select.Option value='canceled'>Đã huỷ</Select.Option>
                  <Select.Option value='finalized'>Đã hoàn tất</Select.Option>
                  <Select.Option value='all'>Tất cả</Select.Option>
                </Select>
              </div>
            </Col>

            <Col span={12}>
              <div>
                <label>Ngày tạo</label>
                <div>
                  <RangePicker
                    format='DD/MM/YY'
                    presets={rangePickerPresets || [
                      { label: 'Hôm nay', value: [dayjs().add(0, 'd'), dayjs().add(0, 'd')] },
                      { label: 'Hôm qua', value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
                      { label: '7 ngày qua', value: [dayjs().add(-7, 'd'), dayjs().add(0, 'd')] },
                      { label: '30 ngày qua', value: [dayjs().add(-30, 'd'), dayjs().add(0, 'd')] },
                    ]}
                    value={rangePickerValue}
                    onChange={onDateRangeFilterChange}
                  />
                </div>

              </div>
            </Col>

            <Col span={12}>
              <div>
                <label>Trạng thái thanh toán</label>
                <Select
                  value={filter.payment_status_id ?? 'all'}
                  style={{ width: '100%' }}
                  onChange={(value) => setFilterValue('payment_status_id', value)}
                >
                  {paymentStatusOptions.map((item) => (
                    <Select.Option key={item.id} value={item.id}>{item.title}</Select.Option>
                  ))}
                </Select>
              </div>
            </Col>

            <Col span={12}>
              <div>
                <label>Trạng thái vận chuyển</label>
                <Select
                  value={filter.shipping_status_id ?? 'all'}
                  style={{ width: '100%' }}
                  onChange={(value) => setFilterValue('shipping_status_id', value)}
                >
                  <Select.Option value='all'>Tất cả</Select.Option>
                  {shippingStatusOptions.map((item) => (
                    <Select.Option key={item.id} value={item.id}>{item.title}</Select.Option>
                  ))}
                </Select>
              </div>
            </Col>

            <Col span={12}>
              <div>
                <label>Trạng thái nội bộ</label>
                <Select
                  value={filter.internal_status_id ?? 'all'}
                  style={{ width: '100%' }}
                  onChange={(value) => setFilterValue('internal_status_id', value)}
                >
                  <Select.Option value='all'>Tất cả</Select.Option>
                  {(internalStatuses || []).map((item) => (
                    <Select.Option key={item.id} value={item.id}>{item.title}</Select.Option>
                  ))}
                </Select>
              </div>
            </Col>

            <Col span={24}>
              <div>
                <label>Sản phẩm</label>
                <ProductPicker
                  value={filter.product_ids || []}
                  onChange={(value) => setFilterValue('product_ids', value)}
                  mode='multiple'
                  placeholder='Chọn sản phẩm'
                />
              </div>
            </Col>
          </Row>

          <Flex justify='space-between'>
            <Button onClick={onClearFilters}>Clear</Button>
            <Button type='primary' onClick={() => setShowFilterSheet(false)}>Xong</Button>
          </Flex>
        </Space>
      </Drawer>
    </Space>
  );
};

export default ListMobile;
